import React from "react";
import { IoCheckmarkSharp, IoTimeOutline } from "react-icons/io5";
import "./OrderTimeline.css";

const OrderTimeline = ({ orderData }) => {
  const order = orderData?.data?.order;
  const statusHistory = order?.status_history || [];
  const orderType = order?.type?.value || "service";

  // Logic for Package vs Food Order
  let orderStatuses = 
    orderType === "package" 
      ? ["ready_for_delivery", "on_the_way", "delivered"]
      : ["pending", "confirmed", "processing", "ready_for_delivery", "on_the_way", "delivered"];

  const getStatusLabel = (status) => {
    const labels = {
      pending: "بانتظار المراجعة",
      confirmed: "تم تأكيد الطلب",
      processing: "جار تجهيز الطلب",
      ready_for_delivery: "جاهز للاستلام",
      on_the_way: "في طريق التوصيل",
      delivered: "تم تسليم الطلب",
    };
    return labels[status] || status;
  };
     
  const steps = orderStatuses.map((status, index) => {
    const statusRecord = statusHistory?.find((s) => s.status.value === status);
    return {
      status,
      label: getStatusLabel(status),
      time: statusRecord 
        ? new Date(statusRecord.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) 
        : "--:--",
      completed: !!statusRecord,
      stepNumber: index + 1,
      note: statusRecord?.note || ""
    };
  });

  // Calculate Progress
  const lastCompletedIndex = steps.findLastIndex(s => s.completed);
  const progressWidth = steps.length > 1 
    ? (Math.max(0, lastCompletedIndex) / (steps.length - 1)) * 100 
    : 0;

  if (!order) {
    return (
      <div className="card shadow-sm border-0 rounded-4 order-timeline-card">
        <div className="card-body p-4">
          <div className="text-center text-muted py-3">
            لا توجد بيانات طلب متاحة
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card shadow-sm border-0 rounded-4 order-timeline-card">
      <div className="card-body p-4">
        {/* Header with React Icon */}
        <div className="d-flex align-items-center mb-3">
          <IoTimeOutline className="text-success me-2 fs-4" />
          <h6 className="mb-0 fw-bold timeline-title">الجدول الزمني للطلب</h6>
          <span className="badge bg-primary ms-2">{orderType === "package" ? "طلب توصيل" : "طلب خدمة"}</span>
        </div>

        {/* Current Status */}
        <div className="mb-4">
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted small">الحالة الحالية:</span>
            <span className="badge bg-primary">{order.status?.label || "غير محدد"}</span>
            {order.updated_at && (
              <span className="text-muted small">
                (تم التحديث: {new Date(order.updated_at).toLocaleString()})
              </span>
            )}
          </div>
        </div>

        <div className="timeline-container">
          {/* Steps */}
          <div className="d-flex justify-content-between align-items-start position-relative">
            {steps.map((step, index) => (
              <div key={index} className="step-item text-center">
                <div className={`step-node ${step.completed ? "active" : ""}`}>
                  {step.completed ? (
                    <IoCheckmarkSharp size={20} />
                  ) : (
                    <span className="step-number">{step.stepNumber}</span>
                  )}
                </div>
                
                <div className={`step-label-container mt-3`}>
                  <p className={`mb-0 step-label ${step.completed ? "text-dark fw-bold" : "text-muted"}`}>
                    {step.label}
                  </p>
                  <span className="step-time text-smoke small">{step.time}</span>
                  {step.note && (
                    <div className="step-note text-smoke small mt-1">
                      {step.note}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>  
          
          {/* Progress Bar Track */}
          <div className="timeline-track-bg">
            <div 
              className="timeline-track-fill" 
              style={{ width: `${progressWidth}%` }}
            ></div>
          </div>
        </div>
        
        {/* Status History Details */}
        {statusHistory.length > 0 && (
          <div className="mt-4">
            <h6 className="text-muted small mb-2">تفاصيل الحالة</h6>
            <div className="status-history-list">
              {statusHistory.slice().reverse().map((history, index) => (
                <div key={index} className="status-item d-flex align-items-center justify-content-between py-2 border-bottom mb-2">
                  <div>
                    <span className="badge bg-light text-dark me-2">{history.status.label}</span>
                    {history.note && <span className="text-smoke small">({history.note})</span>}
                  </div>
                  <span className="text-smoke small">
                    {new Date(history.created_at).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderTimeline;
